import React, { useState, useRef, useEffect } from "react";
import VaccinationService from '../services/vaccinationService';
import {API_BASE_URL, API_PATIENT_LIST, API_DOCTOR_LIST } from '../../../helpers/urlHelper';

const CreateVaccinationOffcanvas = () => {
  const offcanvasCloseRef = useRef(null);

  // State for doctors and patients
  const [doctors, setDoctors] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);

  // Empty initial state for new vaccination
  const emptyVaccinationState = {
    vaccineId: '',
    vaccineName: '',
    patientId: '',
    dateAdministered: '',
    doseNumber: 1,
    administrationRoute: 'Intramuscular',
    administrationSite: 'Deltoid muscle',
    lotNumber: '',
    manufacturerId: '',
    expirationDate: '',
    administratorId: '',
    status: 'Completed',
    nextDueDate: '',
    notes: ''
  };

  const [vaccination, setVaccination] = useState(emptyVaccinationState);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Predefined vaccines list
  const VACCINES = [
    { id: 'VAC001', name: 'COVID-19 Pfizer' },
    { id: 'VAC002', name: 'COVID-19 Moderna' },
    { id: 'VAC003', name: 'COVID-19 AstraZeneca' },
    { id: 'VAC004', name: 'Influenza (Flu)' },
    { id: 'VAC005', name: 'Hepatitis B' },
    { id: 'VAC006', name: 'Hepatitis A' },
    { id: 'VAC007', name: 'Tetanus' },
    { id: 'VAC008', name: 'Measles, Mumps, Rubella (MMR)' },
    { id: 'VAC009', name: 'Varicella (Chickenpox)' },
    { id: 'VAC010', name: 'Pneumococcal' },
    { id: 'VAC011', name: 'Meningococcal' },
    { id: 'VAC012', name: 'HPV (Human Papillomavirus)' },
    { id: 'VAC013', name: 'Shingles (Zoster)' },
    { id: 'VAC014', name: 'Polio' },
    { id: 'VAC015', name: 'Rabies' }
  ];

  // Administration routes
  const ADMINISTRATION_ROUTES = [
    'Intramuscular',
    'Subcutaneous',
    'Intradermal',
    'Oral',
    'Intranasal'
  ];

  // Administration sites
  const ADMINISTRATION_SITES = [
    'Deltoid muscle',
    'Vastus lateralis',
    'Anterolateral thigh',
    'Buttocks',
    'Arm (upper)',
    'Oral cavity',
    'Nasal cavity'
  ];

  // Vaccination status options
  const STATUS_OPTIONS = [
    'Completed',
    'Scheduled',
    'Cancelled',
    'Postponed'
  ];

  // Predefined manufacturers
  const MANUFACTURERS = [
    { id: 'MFR001', name: 'Pfizer-BioNTech' },
    { id: 'MFR002', name: 'Moderna' },
    { id: 'MFR003', name: 'AstraZeneca' },
    { id: 'MFR004', name: 'Johnson & Johnson' },
    { id: 'MFR005', name: 'Sanofi' },
    { id: 'MFR006', name: 'GSK (GlaxoSmithKline)' },
    { id: 'MFR007', name: 'Merck' },
    { id: 'MFR008', name: 'Novavax' }
  ];

  // Función para obtener los doctores desde la API
  const fetchDoctors = async () => {
    try {
      const doctorsResponse = await fetch(`${API_DOCTOR_LIST}`);
      if (!doctorsResponse.ok) {
        throw new Error('Failed to fetch doctors');
      }
      const doctorsData = await doctorsResponse.json();
      
      console.log('Raw doctors response:', doctorsData);
      
      const doctorsArray = doctorsData && doctorsData.data ? doctorsData.data : [];
      
      console.log('Processed doctors array:', doctorsArray);
      return doctorsArray;
    } catch (err) {
      console.error('Error fetching doctors:', err);
      return [];
    }
  };

  // Función para obtener los pacientes desde la API
  const fetchPatients = async () => {
    try {
      const patientsResponse = await fetch(`${API_BASE_URL}${API_PATIENT_LIST}`);
      if (!patientsResponse.ok) {
        throw new Error('Failed to fetch patients');
      }
      const patientsData = await patientsResponse.json();
      
      console.log('Raw patients response:', patientsData);
      
      const patientsArray = patientsData && patientsData.data ? patientsData.data : [];
      
      console.log('Processed patients array:', patientsArray);
      return patientsArray;
    } catch (err) {
      console.error('Error fetching patients:', err);
      return [];
    }
  };

  // Reset form to empty state
  const resetOffcanvas = () => {
    setVaccination(emptyVaccinationState);
    setError(null);
    setSuccessMessage(null);
  };
  
  // Load data when component mounts
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const doctorsData = await fetchDoctors();
        const patientsData = await fetchPatients();
        
        setDoctors(doctorsData);
        setPatients(patientsData);
        
        setLoading(false);
      } catch (error) {
        console.error('Error loading data:', error);
        setError('Failed to load form data.');
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setVaccination(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleVaccineChange = (e) => {
    const selectedVaccineId = e.target.value;
    const selectedVaccine = VACCINES.find(v => v.id === selectedVaccineId);
    
    setVaccination(prev => ({
      ...prev,
      vaccineId: selectedVaccineId,
      vaccineName: selectedVaccine ? selectedVaccine.name : ''
    }));
  };

  const validateForm = () => {
    if (!vaccination.patientId) {
      setError('Please select a patient');
      return false;
    }
    if (!vaccination.administratorId) {
      setError('Please select an administrator (doctor)');
      return false;
    }
    if (!vaccination.vaccineId) {
      setError('Please select a vaccine');
      return false;
    }
    if (!vaccination.dateAdministered) {
      setError('Please select administration date');
      return false;
    }
    if (!vaccination.lotNumber) {
      setError('Please enter lot number');
      return false;
    }
    if (!vaccination.expirationDate) {
      setError('Please enter expiration date');
      return false;
    }
    if (!vaccination.manufacturerId) {
      setError('Please select manufacturer');
      return false;
    }
    return true;
  };

  const saveVaccination = async () => {
    setError(null);
    setSuccessMessage(null);
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      
      // Format dates to ISO string
      const vaccinationData = {
        ...vaccination,
        dateAdministered: new Date(vaccination.dateAdministered).toISOString(),
        expirationDate: new Date(vaccination.expirationDate).toISOString(),
        nextDueDate: vaccination.nextDueDate ? new Date(vaccination.nextDueDate).toISOString() : null,
        doseNumber: parseInt(vaccination.doseNumber)
      };
      
      console.log('Sending vaccination data:', JSON.stringify(vaccinationData, null, 2));
      
      const response = await VaccinationService.createVaccination(vaccinationData);
      
      console.log('Vaccination created successfully:', response);
      
      setSuccessMessage('Vaccination record successfully created!');
      
      setTimeout(() => {
        if (offcanvasCloseRef.current) {
          offcanvasCloseRef.current.click();
        }
        window.location.reload();
      }, 500);
      
    } catch (error) {
      console.error('Error creating vaccination:', error);
      console.error('Error details:', error.response?.data || error.message);
      
      if (error.response?.data?.message) {
        setError(`Error: ${error.response.data.message}`);
      } else if (error.message) {
        setError(`Error: ${error.message}`);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOffcanvasInit = () => {
    resetOffcanvas();
  };

  // Get current date for date input defaults
  const getCurrentDate = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  };

  const getCurrentDateOnly = () => {
    const now = new Date();
    return now.toISOString().slice(0, 10);
  };

  return (
    <>
      <button
        type="button"
        className="btn btn-primary addnewbtn d-flex align-items-center gap-1" 
        data-bs-toggle="offcanvas"
        data-bs-target="#vaccinationOffcanvas"
        aria-controls="vaccinationOffcanvas"
        onClick={handleOffcanvasInit}
      >
        <span className="d-block material-symbols-outlined icon-md">add</span>
                                        <span className="d-block">Add New</span>
      </button>

      <div
        className="offcanvas offcanvas-end"
        tabIndex="-1"
        id="vaccinationOffcanvas"
        aria-labelledby="vaccinationOffcanvasLabel"
        data-bs-backdrop="false"
        style={{ width: '700px' }}
      >
        <div className="offcanvas-header border-bottom">
          <h5 className="offcanvas-title" id="vaccinationOffcanvasLabel">
            Create Vaccination Record
          </h5>
          <button
            type="button"
            className="btn-close"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
            ref={offcanvasCloseRef}
          ></button>
        </div>
        
        <div className="offcanvas-body">
          {loading && (
            <div className="d-flex justify-content-center my-3">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}
          
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}
          
          {successMessage && (
            <div className="alert alert-success" role="alert">
              {successMessage}
            </div>
          )}
          
          <div className="vaccination-form">
            {/* Patient and Administrator Section */}
            <div className="mb-4">
              <h6 className="text-primary border-bottom pb-2 mb-3">Patient & Administrator</h6>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label htmlFor="patientId" className="form-label">Patient *</label>
                  <select
                    className="form-select"
                    id="patientId"
                    name="patientId"
                    value={vaccination.patientId}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select patient</option>
                    {patients.map(patient => (
                      <option key={patient._id} value={patient._id}>
                        {patient.name} {patient.surname || patient.lastName || ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6 mb-3">
                  <label htmlFor="administratorId" className="form-label">Administrator (Doctor) *</label>
                  <select
                    className="form-select"
                    id="administratorId"
                    name="administratorId"
                    value={vaccination.administratorId}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select administrator</option>
                    {doctors.map(doctor => (
                      <option key={doctor._id} value={doctor._id}>
                        Dr. {doctor.name} {doctor.surname || doctor.lastName || ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Vaccine Information Section */}
            <div className="mb-4">
              <h6 className="text-primary border-bottom pb-2 mb-3">Vaccine Information</h6>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label htmlFor="vaccineId" className="form-label">Vaccine *</label>
                  <select
                    className="form-select"
                    id="vaccineId"
                    name="vaccineId"
                    value={vaccination.vaccineId}
                    onChange={handleVaccineChange}
                    required
                  >
                    <option value="">Select vaccine</option>
                    {VACCINES.map(vaccine => (
                      <option key={vaccine.id} value={vaccine.id}>
                        {vaccine.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6 mb-3">
                  <label htmlFor="manufacturerId" className="form-label">Manufacturer *</label>
                  <select
                    className="form-select"
                    id="manufacturerId"
                    name="manufacturerId"
                    value={vaccination.manufacturerId}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select manufacturer</option>
                    {MANUFACTURERS.map(manufacturer => (
                      <option key={manufacturer.id} value={manufacturer.id}>
                        {manufacturer.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label htmlFor="lotNumber" className="form-label">Lot Number *</label>
                  <input
                    type="text"
                    className="form-control"
                    id="lotNumber"
                    name="lotNumber"
                    value={vaccination.lotNumber}
                    onChange={handleInputChange}
                    placeholder="Enter lot number"
                    required
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label htmlFor="expirationDate" className="form-label">Expiration Date *</label>
                  <input
                    type="date"
                    className="form-control"
                    id="expirationDate"
                    name="expirationDate"
                    value={vaccination.expirationDate}
                    onChange={handleInputChange}
                    min={getCurrentDateOnly()}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Administration Details Section */}
            <div className="mb-4">
              <h6 className="text-primary border-bottom pb-2 mb-3">Administration Details</h6>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label htmlFor="dateAdministered" className="form-label">Date Administered *</label>
                  <input
                    type="datetime-local"
                    className="form-control"
                    id="dateAdministered"
                    name="dateAdministered"
                    value={vaccination.dateAdministered}
                    onChange={handleInputChange}
                    max={getCurrentDate()}
                    required
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label htmlFor="doseNumber" className="form-label">Dose Number</label>
                  <input
                    type="number"
                    className="form-control"
                    id="doseNumber"
                    name="doseNumber"
                    value={vaccination.doseNumber}
                    onChange={handleInputChange}
                    min="1"
                    max="10"
                  />
                </div>
              </div>
              
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label htmlFor="administrationRoute" className="form-label">Administration Route</label>
                  <select
                    className="form-select"
                    id="administrationRoute"
                    name="administrationRoute"
                    value={vaccination.administrationRoute}
                    onChange={handleInputChange}
                  >
                    {ADMINISTRATION_ROUTES.map(route => (
                      <option key={route} value={route}>
                        {route}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6 mb-3">
                  <label htmlFor="administrationSite" className="form-label">Administration Site</label>
                  <select
                    className="form-select"
                    id="administrationSite"
                    name="administrationSite"
                    value={vaccination.administrationSite}
                    onChange={handleInputChange}
                  >
                    {ADMINISTRATION_SITES.map(site => (
                      <option key={site} value={site}>
                        {site}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label htmlFor="status" className="form-label">Status</label>
                  <select
                    className="form-select"
                    id="status"
                    name="status"
                    value={vaccination.status}
                    onChange={handleInputChange}
                  >
                    {STATUS_OPTIONS.map(status => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6 mb-3">
                  <label htmlFor="nextDueDate" className="form-label">Next Due Date</label>
                  <input
                    type="date"
                    className="form-control"
                    id="nextDueDate"
                    name="nextDueDate"
                    value={vaccination.nextDueDate}
                    onChange={handleInputChange}
                    min={getCurrentDateOnly()}
                  />
                </div>
              </div>
            </div>

            {/* Additional Information Section */}
            <div className="mb-4">
              <h6 className="text-primary border-bottom pb-2 mb-3">Additional Information</h6>
              <div className="row">
                <div className="col-12 mb-3">
                  <label htmlFor="notes" className="form-label">Notes</label>
                  <textarea
                    className="form-control"
                    id="notes"
                    name="notes"
                    value={vaccination.notes}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="Enter any additional notes about the vaccination..."
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="d-flex gap-2 pt-3 border-top">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="offcanvas"
              >
                Close
              </button>
              <button 
                type="button" 
                className="btn btn-success flex-grow-1"
                onClick={saveVaccination}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Vaccination'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateVaccinationOffcanvas;